import type { ExpressionDescriptor, FormFieldDescriptor, PrimitiveValue } from '@finance/contracts';

export type ComputedValueFn = (
  getField: (fieldId: string) => unknown,
) => unknown;

const computedFns = new Map<string, ComputedValueFn>();

export function registerComputedValue(computedId: string, fn: ComputedValueFn): void {
  computedFns.set(computedId, fn);
}

export function getComputedValueFn(computedId: string): ComputedValueFn | undefined {
  return computedFns.get(computedId);
}

export function evaluateExpression(
  expr: ExpressionDescriptor,
  values: Record<string, unknown>,
  fields: FormFieldDescriptor[],
): unknown {
  switch (expr.type) {
    case 'fieldRef': {
      if (!expr.fieldId) return undefined;
      const field = fields.find((f) => f.id === expr.fieldId);
      const fieldName = field?.name ?? (expr.fieldId as string);
      return values[fieldName];
    }
    case 'constant':
      return expr.value;
    case 'computed': {
      if (!expr.computedId) return undefined;
      const fn = getComputedValueFn(expr.computedId);
      if (!fn) return undefined;
      const getField = (fieldId: string) => {
        const field = fields.find((f) => f.id === fieldId || f.name === fieldId);
        const fieldName = field?.name ?? fieldId;
        return values[fieldName];
      };
      return fn(getField);
    }
    default:
      return undefined;
  }
}

export function getComputedFields(fields: FormFieldDescriptor[]): FormFieldDescriptor[] {
  return fields.filter((f) => f.value?.computedValueExpression);
}

export function resolveComputedValues(
  values: Record<string, unknown>,
  fields: FormFieldDescriptor[],
): Record<string, unknown> {
  const result = { ...values };
  const computedFields = getComputedFields(fields);

  for (const field of computedFields) {
    const expr = field.value!.computedValueExpression!;
    const computedValue = evaluateExpression(expr, result, fields);
    if (computedValue !== undefined) {
      result[field.name] = computedValue;
    }
  }

  return result;
}

registerComputedValue('sum', (getField) => {
  const a = Number(getField('a') ?? 0);
  const b = Number(getField('b') ?? 0);
  return a + b;
});

registerComputedValue('product', (getField) => {
  const a = Number(getField('a') ?? 0);
  const b = Number(getField('b') ?? 0);
  return a * b;
});

registerComputedValue('percentage', (getField) => {
  const part = Number(getField('part') ?? 0);
  const whole = Number(getField('whole') ?? 0);
  if (whole === 0) return 0;
  return (part / whole) * 100;
});
